import type { z } from 'zod';
import { CSRF_COOKIE, CSRF_HEADER, type ApiError } from '@apen/shared';

/**
 * Cliente HTTP da aplicacao.
 *
 * Duas responsabilidades que nenhuma tela deve reimplementar:
 *
 * 1. Repetir o cookie de CSRF no cabecalho em toda escrita (double-submit).
 * 2. Transformar erro da API em excecao com mensagem legivel, para que o card
 *    de feedback tenha sempre o que mostrar. Nao existe `catch` silencioso.
 */

export class ApiRequestError extends Error {
  readonly status: number;
  readonly detail: string | undefined;

  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.detail = detail;
  }

  /** Sessao expirada ou ausente: a UI deve mandar de volta para o login. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

function readCookie(name: string): string | null {
  const match = new RegExp(`(?:^|;\\s*)${name}=([^;]*)`).exec(document.cookie);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Devolve o token de CSRF, buscando um novo se não houver.
 *
 * O servidor emite o cookie em qualquer requisição que chegue sem um válido,
 * então basta uma leitura barata para recuperá-lo. Isso cobre os casos em que o
 * navegador fica sem token com a aba aberta — o cookie expira junto com a
 * sessão, de 12 em 12 horas — sem transformar isso num erro que a pessoa não
 * tem como resolver a não ser recarregando.
 */
let buscaEmAndamento: Promise<string | null> | null = null;

async function obterTokenCsrf(): Promise<string | null> {
  const atual = readCookie(CSRF_COOKIE);
  if (atual) return atual;

  // Se duas escritas partirem juntas sem token, uma única ida ao servidor
  // atende as duas.
  buscaEmAndamento ??= (async () => {
    try {
      await fetch('/api/me', { method: 'GET', credentials: 'same-origin' });
    } catch {
      // Sem rede não há o que recuperar; o erro real aparece na chamada
      // seguinte, que é a que a pessoa realmente pediu.
    }
    return readCookie(CSRF_COOKIE);
  })().finally(() => {
    buscaEmAndamento = null;
  });

  return buscaEmAndamento;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Quando presente, valida a resposta e devolve o tipo do schema. */
  schema?: z.ZodType<unknown>;
  /** Cabecalhos extras — usado pelo modo admin (`x-admin-pin`). */
  headers?: Record<string, string>;
  /** Devolve a `Response` crua em vez de parsear — para download de arquivo. */
  raw?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { ...options.headers };

  const isFormData = options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  if (!SAFE_METHODS.has(method)) {
    const csrf = await obterTokenCsrf();
    if (!csrf) {
      throw new ApiRequestError(
        0,
        'Não foi possível validar a segurança da sessão.',
        'Recarregue a página e tente de novo.',
      );
    }
    headers[CSRF_HEADER] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      // Os cookies de sessao e de CSRF precisam acompanhar a requisicao.
      credentials: 'same-origin',
      body: isFormData ? (options.body as FormData) : options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal ?? null,
    });
  } catch (cause) {
    throw new ApiRequestError(0, 'Nao foi possivel falar com o servidor.', 'Verifique sua conexao e tente de novo.');
  }

  if (response.status === 204) return undefined as T;

  // Resposta binaria: so o corpo de SUCESSO escapa do parse. O erro continua
  // descendo para o bloco abaixo, que transforma o JSON da API em
  // ApiRequestError — um download que falha precisa dizer por que tanto quanto
  // qualquer outra chamada, e a exportacao contabil recusa com a contagem de
  // pendencias no corpo.
  if (options.raw && response.ok) return response as T;

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const error = (isJson ? (payload as ApiError) : null);
    const message = error?.message ?? `Falha na requisicao (${String(response.status)}).`;
    const detail = error?.issues?.map((issue) => issue.message).join(' ');
    throw new ApiRequestError(response.status, message, detail);
  }

  if (!options.schema) return payload as T;

  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) {
    // Divergencia entre o que a API devolve e o contrato compartilhado. Vale
    // falhar alto: seguir com dado fora do formato produz tela quebrada mais
    // adiante, longe da causa.
    console.error('Resposta fora do contrato em %s', path, parsed.error.issues);
    throw new ApiRequestError(
      response.status,
      'O servidor respondeu num formato inesperado.',
      'A pagina pode estar desatualizada. Recarregue e tente de novo.',
    );
  }
  return parsed.data as T;
}

export const api = {
  get: <T>(path: string, schema?: z.ZodType<unknown>, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', schema, signal }),

  post: <T>(path: string, body?: unknown, schema?: z.ZodType<unknown>) =>
    request<T>(path, { method: 'POST', body, schema }),

  put: <T>(
    path: string,
    body?: unknown,
    schema?: z.ZodType<unknown>,
    headers?: Record<string, string>,
  ) => request<T>(path, { method: 'PUT', body, schema, headers }),

  patch: <T>(path: string, body?: unknown, schema?: z.ZodType<unknown>) =>
    request<T>(path, { method: 'PATCH', body, schema }),

  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),

  /**
   * Baixa um arquivo, com o mesmo tratamento de erro das demais chamadas.
   *
   * Um `<a href>` apontando direto para a rota seria mais curto, mas um 400 ou
   * 403 abriria JSON cru numa aba nova em vez de virar card de feedback.
   */
  blob: async (path: string): Promise<{ blob: Blob; filename: string | null }> => {
    const response = await request<Response>(path, { method: 'GET', raw: true });
    return { blob: await response.blob(), filename: nomeDoAnexo(response) };
  },

  /** Download cujo conteudo depende do que a tela enviar — exportacao editada. */
  blobPost: async (
    path: string,
    body: unknown,
  ): Promise<{ blob: Blob; filename: string | null }> => {
    const response = await request<Response>(path, { method: 'POST', body, raw: true });
    return { blob: await response.blob(), filename: nomeDoAnexo(response) };
  },
};

/** Nome do arquivo anunciado pelo servidor no `Content-Disposition`. */
function nomeDoAnexo(response: Response): string | null {
  const header = response.headers.get('content-disposition');
  const match = header ? /filename="?([^";]+)"?/i.exec(header) : null;
  return match?.[1] ?? null;
}
